import { Selector, State } from '@ngxs/store';
import { IPlaylist } from '@interfaces/playlist';
import { Injectable } from '@angular/core';
import { PlaylistsService } from '@services/playlists/playlists.service';
import { Observable } from 'rxjs';
import { IResponse } from '@interfaces/response';
import { tap } from 'rxjs/operators';
import { SidebarPlaylistsStateModel } from '@store/sidebar-state/playlists/sidebar.playlists-state.model';
import { itemsPerPage } from '@shared/helpers';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { DataActionWithoutSubscription } from '@shared/functions';

@StateRepository()
@State<SidebarPlaylistsStateModel>({
    name: 'SidebarMainPlaylistTab',
    defaults: {
        currentPage: 1,
        playlistPerPage: itemsPerPage[5],
        totalPlaylists: 0,
        loader: false,
        playlists: [],
        activePlaylist: null,
        searchParam: '',
    }
})


@Injectable()
export class SidebarPlaylistsState extends NgxsDataRepository<SidebarPlaylistsStateModel> {
    constructor(private playlistService: PlaylistsService ) {
        super();
    }

    @Selector([SidebarPlaylistsState])
    static activePlaylist(state: SidebarPlaylistsStateModel): IPlaylist | null {
        return state.activePlaylist;
    }

    @DataActionWithoutSubscription({cancelUncompleted: true})
    getSidebarPlaylists(): Observable<IResponse<IPlaylist>> {
        const state = this.ctx.getState();

        if (!state) {
            return this.playlistService.fetchPlaylists({
                limit: itemsPerPage[5],
                skip: 0,
                search: '',
                type: 'main'
            });
        }

        const limit = state.playlistPerPage;
        const skip = limit * (state.currentPage - 1);
        const search = state.searchParam;

        this.ctx.patchState({loader: true});
        return this.playlistService.fetchPlaylists({
            limit,
            skip,
            search,
            type: 'main'
        }).pipe(
            tap(data => {
                this.ctx.patchState({playlists: data.rows, totalPlaylists: data.total, loader: false});
            })
        );
    }

    @DataActionWithoutSubscription()
    getPlaylistsByPage(@Payload('[Sidebar media main playlists] get playlists by page') page: number): Observable<any> {
        this.ctx.patchState({currentPage: page});
        return this.getSidebarPlaylists();
    }

    @DataActionWithoutSubscription()
    setActivePlaylist(@Payload('[Sidebar media main playlists] set active playlist') playlist: IPlaylist): void {
        this.ctx.patchState({activePlaylist: playlist});
    }

    @DataActionWithoutSubscription()
    setSidebarPlaylistPerPage(@Payload('[Sidebar media main playlists] set playlist per page') playlistPerPage: number): Observable<any> {
        this.ctx.patchState({playlistPerPage, currentPage: 1});
        return this.getSidebarPlaylists();
    }

    @DataActionWithoutSubscription()
    setSidebarSearchParam(@Payload('[Sidebar media main playlists] set search param') searchParam: string): Observable<any> {
        this.ctx.patchState({currentPage: 1, searchParam});
        return this.getSidebarPlaylists();
    }

    @DataActionWithoutSubscription()
    refreshAfterDelete(
        @Payload('[Sidebar media main playlists] refresh after delete') deletedCount = 1
    ): Observable<any> {
        const state = this.ctx.getState();
        const totalAfterDelete = Math.max(state.totalPlaylists - deletedCount, 0);
        const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / state.playlistPerPage), 1);
        const currentPage = Math.min(state.currentPage, lastAvailablePage);

        this.ctx.patchState({currentPage});
        return this.getSidebarPlaylists();
    }
}
